import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, ago, fmtDT } from "@/lib/api";

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const click = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.link) nav(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative rounded-full p-2 text-slate-600 transition-colors hover:bg-slate-100">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notification-unread-count" className="num absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0 sm:w-[400px]" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-bold">Notifications <span className="text-muted-foreground">({data.unread} unread)</span></p>
          <button data-testid="notification-mark-all-read" onClick={readAll} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto scroll-thin">
          {data.items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">You're all caught up</p>}
          {data.items.map((n) => (
            <button key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => click(n)}
              className={`flex w-full gap-3 border-b px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-emerald-50/60"}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-primary"}`} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-slate-800">{n.message}</span>
                <span className="mt-1 flex flex-wrap gap-x-2 text-[11px] text-muted-foreground">
                  {n.client && <span className="font-semibold">{n.client}</span>}
                  {n.salesperson && <span>· {n.salesperson}</span>}
                  <span title={fmtDT(n.created_at)}>· {ago(n.created_at)}</span>
                </span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
